import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Zap } from 'lucide-react';

const floatingOrbs = [
  { size: 400, x: '70%', y: '10%', color: 'rgba(255,107,0,0.08)', delay: 0 },
  { size: 300, x: '10%', y: '60%', color: 'rgba(255,107,0,0.06)', delay: 1 },
  { size: 200, x: '85%', y: '70%', color: 'rgba(255,107,0,0.05)', delay: 2 },
];

export default function Hero() {
  return (
    <section id="home" style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', overflow: 'hidden', paddingTop: '5rem', background: 'var(--navy)', color: 'white' }}>
      {/* Background orbs */}
      {floatingOrbs.map((orb, i) => (
        <motion.div
          key={i}
          animate={{ y: [0, -30, 0], scale: [1, 1.05, 1] }}
          transition={{ duration: 8 + i * 2, repeat: Infinity, ease: 'easeInOut', delay: orb.delay }}
          style={{
            position: 'absolute',
            width: orb.size, height: orb.size,
            borderRadius: '50%',
            left: orb.x, top: orb.y,
            background: `radial-gradient(circle, ${orb.color}, transparent 70%)`,
            filter: 'blur(40px)',
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Grid lines */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `linear-gradient(rgba(255,107,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,0,0.03) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 2rem', width: '100%', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'center' }}>
          {/* Left: Text */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.4rem 1rem', borderRadius: 100,
                background: 'var(--electric-dim)',
                border: '1px solid var(--gray-light)',
                marginBottom: '1.5rem',
              }}
            >
              <Zap size={12} color="var(--orange)" fill="var(--orange)" />
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--white)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Technology for You
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', marginBottom: '1.5rem', letterSpacing: '-0.02em', color: 'white' }}
            >
              Powering Zambia's{' '}
              <span style={{
                background: 'linear-gradient(135deg, var(--orange), var(--orange-light))',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                Digital Future
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              style={{ fontSize: '1.1rem', lineHeight: 1.7, color: 'rgba(255, 255, 255, 0.8)', marginBottom: '2.5rem', maxWidth: 480 }}
            >
              Findura Infosys delivers enterprise software, cloud solutions, and IT consulting tailored for African businesses — from Lusaka to the world.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}
            >
              <a
                href="#services"
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.875rem 2rem', borderRadius: 12,
                  background: 'var(--orange)', color: 'var(--white)',
                  fontFamily: 'Syne', fontWeight: 700, fontSize: '0.9rem',
                  boxShadow: '0 0 30px var(--electric-glow)',
                  transition: 'all 0.25s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 50px var(--electric-glow)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 30px var(--electric-glow)'; }}
              >
                Explore Services <ArrowRight size={16} />
              </a>
              <a
                href="#contact"
                style={{
                  padding: '0.875rem 2rem', borderRadius: 12,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: 'white', fontFamily: 'Syne', fontWeight: 600, fontSize: '0.9rem',
                  background: 'transparent', transition: 'all 0.25s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255, 255, 255, 0.1)'; (e.currentTarget as HTMLElement).style.borderColor = 'var(--orange)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255, 255, 255, 0.3)'; }}
              >
                Contact Us
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
              style={{ display: 'flex', gap: '2.5rem', marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}
            >
              {[['5+', 'Service Verticals'], ['20+', 'Trusted Clients'], ['10+', 'Years Experience']].map(([num, label]) => (
                <div key={label}>
                  <div style={{ fontFamily: 'San-serif', fontWeight: 800, fontSize: '2rem', letterSpacing: '-0.03em', color: 'var(--orange)' }}>{num}</div>
                  <div style={{ fontSize: '0.775rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.2rem' }}>{label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: Visual card stack */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ position: 'relative', height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            {/* Connector lines SVG */}
            <svg
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                pointerEvents: 'none', zIndex: 1, opacity: 0.7,
              }}
              viewBox="0 0 600 500"
              preserveAspectRatio="xMidYMid meet"
            >
              {[
                { x1: 300, y1: 250, x2: 80, y2: 100 },
                { x1: 300, y1: 250, x2: 500, y2: 120 },
                { x1: 300, y1: 250, x2: 100, y2: 400 },
                { x1: 300, y1: 250, x2: 510, y2: 410 },
              ].map((line, i) => (
                <motion.line
                  key={i}
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.8 + i * 0.15, ease: 'easeInOut' }}
                  x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
                  stroke="rgba(255,107,0,0.25)"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                />
              ))}
            </svg>

            {/* Main central card */}
            <div style={{
              width: 320, padding: '2rem',
              background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(247,248,252,0.95))',
              border: '1px solid var(--gray-light)',
              borderRadius: 20,
              backdropFilter: 'blur(30px)',
              boxShadow: '0 40px 80px rgba(0,0,0,0.08), 0 0 0 1px rgba(255, 107, 0, 0.1)',
              position: 'relative', zIndex: 3,
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: 14,
                background: 'linear-gradient(135deg, var(--orange), var(--orange-light))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '1.25rem',
                boxShadow: '0 0 20px var(--electric-glow)',
              }}>
                <span style={{ fontSize: '1.5rem' }}>☁️</span>
              </div>
              <div style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.5rem', color: 'var(--text)' }}>Microsoft Cloud Partner</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-soft)', lineHeight: 1.6 }}>Azure • Microsoft 365 • Dynamics 365 • Power Platform</div>
              <div style={{
                marginTop: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap',
              }}>
                {['Azure', 'M365', 'D365'].map(tag => (
                  <span key={tag} style={{
                    padding: '0.25rem 0.75rem', borderRadius: 100, fontSize: '0.7rem',
                    background: 'var(--electric-dim)', color: 'var(--text)',
                    border: '1px solid var(--gray-light)', fontWeight: 600,
                  }}>{tag}</span>
                ))}
              </div>
            </div>

            {/* Floating mini cards */}
            {[
              { icon: '💻', label: 'Software Dev', x: -250, y: -160, delay: 1.2 },
              { icon: '🔒', label: 'IT Security', x: 150, y: -130, delay: 1.4 },
              { icon: '🌐', label: 'Web Services', x: -200, y: 135, delay: 1.6 },
              { icon: '📊', label: 'Business Systems', x: 150, y: 130, delay: 1.8 },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1, y: [0, -8, 0] }}
                transition={{
                  opacity: { delay: card.delay, duration: 0.4 },
                  scale: { delay: card.delay, duration: 0.4 },
                  y: { duration: 4 + i, repeat: Infinity, ease: 'easeInOut', delay: i * 0.5 },
                }}
                style={{
                  position: 'absolute',
                  left: `calc(50% + ${card.x}px)`,
                  top: `calc(50% + ${card.y}px)`,
                  transform: 'translate(-50%, -50%)',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255,255,255,0.95)',
                  border: '1px solid var(--gray-light)',
                  borderRadius: 12,
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  backdropFilter: 'blur(20px)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  zIndex: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: '1.1rem' }}>{card.icon}</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text)' }}>{card.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          #home > div > div { grid-template-columns: 1fr !important; }
          #home > div > div > div:last-child { display: none !important; }
        }
      `}</style>
    </section>
  );
}
